import { useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { aujourdhui } from '../../lib/dates-civiles';
import type { ResultatEva, SaisieEva } from '../../lib/useEva';

const CLASSE_LIBELLE = 'mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';
const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy';

/**
 * Saisie d'un passage du test EVA, ou correction d'un passage saisi. Le score
 * et la date sont contrôlés par l'API ; l'écran n'empêche qu'un envoi vide.
 */
export function ModaleResultatEva({
  resultat,
  formats,
  enCours,
  erreur,
  onEnregistrer,
  onFermer,
}: {
  /** Passage à corriger, `null` pour une nouvelle saisie. */
  resultat: ResultatEva | null;
  formats: string;
  enCours: boolean;
  erreur: string | null;
  onEnregistrer: (saisie: SaisieEva) => void;
  onFermer: () => void;
}): JSX.Element {
  const [score, setScore] = useState(resultat ? String(resultat.score) : '');
  const [passeLe, setPasseLe] = useState(resultat?.passeLe ?? aujourdhui());
  const [commentaire, setCommentaire] = useState(resultat?.commentaire ?? '');
  const [fichier, setFichier] = useState<File | null>(null);
  const [retirerCompteRendu, setRetirerCompteRendu] = useState(false);

  const complet = score.trim() !== '' && passeLe !== '';

  return (
    <Modale
      titre={resultat ? 'Corriger le résultat EVA' : 'Saisir un résultat EVA'}
      sousTitre="Test de repérage de l’illettrisme, passé hors de la plateforme"
      ouverte
      onFermer={onFermer}
    >
      <form
        className="space-y-4 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (complet) onEnregistrer({ score, passeLe, commentaire, fichier, retirerCompteRendu });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Score sur 100</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              step={1}
              required
              value={score}
              onChange={(evenement) => setScore(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Date de passation</span>
            <input
              type="date"
              required
              max={aujourdhui()}
              value={passeLe}
              onChange={(evenement) => setPasseLe(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <label className="block min-w-0">
          <span className={CLASSE_LIBELLE}>Commentaire</span>
          <textarea
            rows={3}
            maxLength={1000}
            value={commentaire}
            onChange={(evenement) => setCommentaire(evenement.target.value)}
            placeholder="Points d’appui, difficultés repérées…"
            className={`${CLASSE_CHAMP} min-h-[88px]`}
          />
        </label>

        <div className="min-w-0">
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>
              {resultat?.compteRendu ? 'Remplacer le compte rendu' : 'Compte rendu de la passation'}
            </span>
            <input
              key={retirerCompteRendu ? 'retrait' : 'depot'}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={retirerCompteRendu}
              onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
              className={`${CLASSE_CHAMP} py-2`}
            />
            <span className="mt-1 block text-[11px] text-codialis-gray">Facultatif · {formats}</span>
          </label>
          {resultat?.compteRendu && (
            <label className="mt-2 flex min-h-[44px] items-center gap-2 text-sm text-codialis-navy">
              <input
                type="checkbox"
                checked={retirerCompteRendu}
                onChange={(evenement) => {
                  setRetirerCompteRendu(evenement.target.checked);
                  if (evenement.target.checked) setFichier(null);
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="min-w-0 break-words">Retirer « {resultat.compteRendu.nomFichier} »</span>
            </label>
          )}
        </div>

        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-wrap gap-2">
          <BoutonPrincipal type="submit" disabled={!complet || enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
        </div>
      </form>
    </Modale>
  );
}
